import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import LogoMark from "./LogoMark";

const NAV = [
  { label: "Solutions", href: "#solutions" },
  { label: "Industries", href: "#industries" },
  { label: "Brands", href: "#brands" },
  { label: "Why Us", href: "#why" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "Contact", href: "#contact" },
];

const scrollTo = (href) => {
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
  else el.scrollIntoView({ behavior: "smooth", block: "start" });
};

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 1, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#222222]/85 backdrop-blur-xl border-b border-[#9A9A9A]/15" : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
        <button
          onClick={() => scrollTo("#top")}
          className="inline-flex items-center gap-3 text-[#FAFAFA] text-[15px] tracking-tight"
          data-testid="logo-home-btn"
          aria-label="DB Computronics home"
        >
          <LogoMark
            size={40}
            withWordmark={true}
            wordmarkClassName="hidden sm:inline text-[16px] font-bold text-[#FAFAFA]"
          />
        </button>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Primary">
          {NAV.map((item) => (
            <button
              key={item.href}
              onClick={() => scrollTo(item.href)}
              className="text-[13px] tracking-[0.15em] uppercase text-[#FAFAFA]/80 hover:text-[#FAFAFA] red-underline font-btn"
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
            >
              {item.label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            onClick={() => scrollTo("#contact")}
            className="btn-red hidden md:inline-flex items-center gap-2 px-5 py-3 rounded-lg text-[12px] tracking-[0.2em] uppercase font-btn"
            data-testid="header-quote-cta"
          >
            <span>Request a Quote</span>
          </button>

          <button
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden text-[#FAFAFA] p-2"
            aria-label={open ? "Close menu" : "Open menu"}
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden overflow-hidden bg-[#222222]/95 backdrop-blur-xl border-t border-[#9A9A9A]/15"
          >
            <div className="px-6 py-6 flex flex-col gap-3">
              {NAV.map((item) => (
                <button
                  key={item.href}
                  onClick={() => {
                    setOpen(false);
                    setTimeout(() => scrollTo(item.href), 200);
                  }}
                  className="text-left text-[#FAFAFA]/90 py-2 text-[15px] font-btn tracking-tight"
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {item.label}
                </button>
              ))}
              <button
                onClick={() => {
                  setOpen(false);
                  setTimeout(() => scrollTo("#contact"), 200);
                }}
                className="btn-red mt-3 px-5 py-3 rounded-lg text-[12px] tracking-[0.2em] uppercase font-btn"
                data-testid="mobile-quote-cta"
              >
                <span>Request a Quote</span>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
